import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import MarketingLayout from '../../components/MarketingLayout';
import SEO from '../../components/SEO';
import { tokens } from '../../components/SEOPageLayout';
import { useToolReadiness } from '../../hooks/useToolReadiness';
import { useAuthStore } from '../../stores/authStore';
import { appLinks } from '../../lib/crossSiteLinks';

interface QuestionnaireData {
  systemName: string;
  systemDescription: string;
  industry: string;
  useCaseCategory: string;
  interactsWithHumans: boolean;
  makesAutomatedDecisions: boolean;
  processesBiometricData: boolean;
  usedInCriticalInfrastructure: boolean;
  usedForEmploymentDecisions: boolean;
  usedForCreditScoring: boolean;
  usedForEducationAssessment: boolean;
  usedForLawEnforcement: boolean;
  usedForBorderControl: boolean;
  usedForJusticeOrDemocracy: boolean;
  manipulativeOrDeceptive: boolean;
  exploitsVulnerabilities: boolean;
  performsSocialScoring: boolean;
}

interface ClassificationResult {
  assessmentId: string;
  riskLevel: number;
  riskLevelDisplay: string;
  applicableArticles: string[];
  requiredDocumentation: string[];
  complianceChecklist: string;
  explanation: string;
  deadlineDate: string;
  daysUntilDeadline: number;
}

const resolveToolAuthState = (
  readinessAuth: boolean | undefined,
  authHydrated: boolean,
  storeAuthenticated: boolean
) => {
  if (readinessAuth !== undefined) {
    return readinessAuth;
  }

  return authHydrated ? storeAuthenticated : undefined;
};

export default function AiRiskClassifierPage() {
  const readiness = useToolReadiness('ai-risk-classifier');
  const authHydrated = useAuthStore((state) => state._hasHydrated);
  const readinessAuth = readiness.data?.isAuthenticated;
  const storeAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthenticated = resolveToolAuthState(readinessAuth, authHydrated, storeAuthenticated);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClassificationResult | null>(null);
  
  const [formData, setFormData] = useState<QuestionnaireData>({
    systemName: '',
    systemDescription: '',
    industry: '',
    useCaseCategory: '',
    interactsWithHumans: false,
    makesAutomatedDecisions: false,
    processesBiometricData: false,
    usedInCriticalInfrastructure: false,
    usedForEmploymentDecisions: false,
    usedForCreditScoring: false,
    usedForEducationAssessment: false,
    usedForLawEnforcement: false,
    usedForBorderControl: false,
    usedForJusticeOrDemocracy: false,
    manipulativeOrDeceptive: false,
    exploitsVulnerabilities: false,
    performsSocialScoring: false,
  });

  const handleInputChange = (field: keyof QuestionnaireData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.systemName || !formData.systemDescription) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/guest/ai-compliance/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.status === 429) {
        const data = await response.json();
        setError(data.message || 'Rate limit exceeded. Please try again later.');
        return;
      }

      if (!response.ok) {
        throw new Error('Classification failed');
      }

      const data = await response.json();
      setResult(data);
      setStep(4); // Show results
    } catch (err) {
      setError('Failed to classify AI system. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level: number) => {
    switch (level) {
      case 1: return 'text-red-400 border-red-400 bg-red-400/10';
      case 2: return 'text-orange-400 border-orange-400 bg-orange-400/10';
      case 3: return 'text-yellow-400 border-yellow-400 bg-yellow-400/10';
      case 4: return 'text-green-400 border-green-400 bg-green-400/10';
      default: return 'text-gray-400 border-gray-400 bg-gray-400/10';
    }
  };

  const pageContent = (
    <>
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-4xl mx-auto">
            
            {/* Progress Indicator */}
            {step < 4 && (
              <div className="mb-12">
                <div className="flex items-center justify-center gap-4">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        s === step ? 'border-primary-400 bg-primary-400/20 text-primary-400' :
                        s < step ? 'border-primary-600 bg-primary-600/20 text-primary-400' :
                        'border-slate-600 text-slate-500'
                      }`}>
                        {s}
                      </div>
                      {s < 3 && (
                        <div className={`w-16 h-0.5 ${s < step ? 'bg-primary-600' : 'bg-slate-700'}`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-sm text-slate-400 max-w-md mx-auto">
                  <span>System Info</span>
                  <span>Risk Factors</span>
                  <span>Context</span>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-400/50 rounded-lg text-red-300">
                {error}
              </div>
            )}

            {/* Step 1: System Description */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">Tell us about your AI system</h2>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    System Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="systemName"
                    value={formData.systemName}
                    onChange={(e) => handleInputChange('systemName', e.target.value)}
                    placeholder="e.g., Customer Support Chatbot"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    System Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.systemDescription}
                    onChange={(e) => handleInputChange('systemDescription', e.target.value)}
                    placeholder="Describe what the AI system does, what decisions it makes, and how it's used..."
                    rows={5}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary-400"
                  />
                  <p className="mt-2 text-sm text-slate-500">Be specific about capabilities, decisions, and user interaction.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Industry</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-primary-400"
                  >
                    <option value="">Select industry...</option>
                    <option value="Technology">Technology / Software</option>
                    <option value="Financial Services">Financial Services</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="E-commerce">E-commerce / Retail</option>
                    <option value="Human Resources">Human Resources</option>
                    <option value="Marketing">Marketing / Advertising</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Transportation">Transportation</option>
                    <option value="Government">Government / Public Sector</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex justify-end gap-4 pt-6">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!formData.systemName || !formData.systemDescription}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Risk Factors */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">Risk Factor Assessment</h2>
                <p className="text-slate-400 mb-8">Select all that apply to your AI system:</p>

                <div className="space-y-4">
                  {[
                    { field: 'interactsWithHumans' as const, label: 'Interacts with humans (chatbot, voice assistant, etc.)', icon: '💬' },
                    { field: 'makesAutomatedDecisions' as const, label: 'Makes automated decisions that affect people', icon: '⚖️' },
                    { field: 'processesBiometricData' as const, label: 'Processes biometric data (facial recognition, fingerprints, voice)', icon: '👤' },
                    { field: 'usedInCriticalInfrastructure' as const, label: 'Used in critical infrastructure (energy, water, transport)', icon: '🏗️' },
                    { field: 'usedForEmploymentDecisions' as const, label: 'Used for hiring, firing, or task assignment decisions', icon: '👔' },
                    { field: 'usedForCreditScoring' as const, label: 'Used for credit scoring or loan decisions', icon: '💳' },
                    { field: 'usedForEducationAssessment' as const, label: 'Used to assess students or determine educational outcomes', icon: '🎓' },
                    { field: 'usedForLawEnforcement' as const, label: 'Used by law enforcement (predictive policing, crime analysis)', icon: '🚔' },
                    { field: 'usedForBorderControl' as const, label: 'Used for migration, asylum, or border control', icon: '🛂' },
                    { field: 'usedForJusticeOrDemocracy' as const, label: 'Used in justice administration or democratic processes', icon: '⚖️' },
                  ].map(({ field, label, icon }) => (
                    <label key={field} className="flex items-start gap-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:border-primary-500/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData[field]}
                        onChange={(e) => handleInputChange(field, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-slate-900"
                      />
                      <span className="flex-1 text-slate-300">
                        <span className="mr-2">{icon}</span>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-between gap-4 pt-6">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Prohibited Practices Check */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">⚠️ Prohibited Practices Check</h2>
                <p className="text-slate-400 mb-8">These practices are BANNED under Article 5. Select any that apply:</p>

                <div className="space-y-4">
                  {[
                    { field: 'performsSocialScoring' as const, label: 'Performs social scoring of individuals based on behavior, social status, or personal characteristics', icon: '🚫' },
                    { field: 'manipulativeOrDeceptive' as const, label: 'Uses subliminal techniques or manipulative/deceptive methods to influence behavior', icon: '🎭' },
                    { field: 'exploitsVulnerabilities' as const, label: 'Exploits vulnerabilities of specific groups (children, elderly, disabled, economically disadvantaged)', icon: '⚠️' },
                  ].map(({ field, label, icon }) => (
                    <label key={field} className="flex items-start gap-4 p-4 bg-red-900/10 border border-red-400/30 rounded-lg cursor-pointer hover:border-red-400/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData[field]}
                        onChange={(e) => handleInputChange(field, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-red-600 text-red-500 focus:ring-red-500 focus:ring-offset-slate-900"
                      />
                      <span className="flex-1 text-slate-300">
                        <span className="mr-2">{icon}</span>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-between gap-4 pt-6">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors"
                  >
                    {loading ? 'Analyzing...' : 'Get Risk Classification →'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Results */}
            {step === 4 && result && (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-white mb-4">Classification Complete</h2>
                  <p className="text-slate-400">
                    {result.daysUntilDeadline} days until EU AI Act enforcement (August 2, 2026)
                  </p>
                </div>

                {/* Risk Level Badge */}
                <div className={`p-8 border-2 rounded-xl ${getRiskColor(result.riskLevel)}`}>
                  <div className="text-center">
                    <div className="text-6xl mb-4">
                      {result.riskLevel === 1 && '🔴'}
                      {result.riskLevel === 2 && '🟠'}
                      {result.riskLevel === 3 && '🟡'}
                      {result.riskLevel === 4 && '🟢'}
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{result.riskLevelDisplay}</h3>
                    <p className="text-lg">{formData.systemName}</p>
                  </div>
                </div>

                {/* Explanation */}
                <div className="p-6 bg-slate-800 border border-slate-700 rounded-lg">
                  <h4 className="font-semibold text-white mb-3">What This Means</h4>
                  <p className="text-slate-300 whitespace-pre-line">{result.explanation}</p>
                </div>

                {/* Applicable Articles */}
                {result.applicableArticles.length > 0 && (
                  <div className="p-6 bg-slate-800 border border-slate-700 rounded-lg">
                    <h4 className="font-semibold text-white mb-3">Applicable EU AI Act Articles</h4>
                    <ul className="space-y-2">
                      {result.applicableArticles.map((article, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300">
                          <span className="text-primary-400 mt-1">•</span>
                          <span>{article}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Required Documentation */}
                {result.requiredDocumentation.length > 0 && (
                  <div className="p-6 bg-slate-800 border border-slate-700 rounded-lg">
                    <h4 className="font-semibold text-white mb-3">Required Documentation</h4>
                    <ul className="space-y-2">
                      {result.requiredDocumentation.map((doc, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300">
                          <span className="text-yellow-400 mt-1">□</span>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Compliance Checklist */}
                <div className="p-6 bg-slate-900 border border-slate-700 rounded-lg">
                  <h4 className="font-semibold text-white mb-4">Detailed Compliance Checklist</h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-slate-300 bg-slate-800/50 p-4 rounded overflow-x-auto">
                      {result.complianceChecklist}
                    </pre>
                  </div>
                </div>

                {/* CTA Section */}
                {result.riskLevel === 2 && ( // High Risk
                  <div className="p-8 bg-gradient-to-r from-primary-900/20 to-primary-800/20 border border-primary-400/30 rounded-xl text-center">
                    <h4 className="text-xl font-bold text-white mb-3">Need Help with Compliance?</h4>
                    <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                      High-risk AI systems require extensive documentation and ongoing monitoring. 
                      EvidentTrail can automate your compliance evidence collection and documentation generation.
                    </p>
                    <div className="flex gap-4 justify-center">
                      <a href={appLinks.register} className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors">
                        Get Started
                      </a>
                      <Link to="/contact" className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors">
                        Talk to an Expert
                      </Link>
                    </div>
                  </div>
                )}

                {/* Start Over */}
                <div className="text-center pt-6">
                  <button
                    onClick={() => {
                      setStep(1);
                      setResult(null);
                      setFormData({
                        systemName: '',
                        systemDescription: '',
                        industry: '',
                        useCaseCategory: '',
                        interactsWithHumans: false,
                        makesAutomatedDecisions: false,
                        processesBiometricData: false,
                        usedInCriticalInfrastructure: false,
                        usedForEmploymentDecisions: false,
                        usedForCreditScoring: false,
                        usedForEducationAssessment: false,
                        usedForLawEnforcement: false,
                        usedForBorderControl: false,
                        usedForJusticeOrDemocracy: false,
                        manipulativeOrDeceptive: false,
                        exploitsVulnerabilities: false,
                        performsSocialScoring: false,
                      });
                    }}
                    className="text-primary-400 hover:text-primary-300 underline"
                  >
                    Classify Another AI System
                  </button>
                  <p className="text-sm text-slate-500 mt-2">Rate limit: 1 assessment per 24 hours</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Info Section */}
      {step < 4 && (
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Why Use This Tool?</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className={tokens.card}>
                  <div className="text-3xl mb-3">⏱️</div>
                  <h4 className="font-semibold text-white mb-2">2-Minute Assessment</h4>
                  <p className="text-sm text-slate-400">Quick questionnaire, instant results</p>
                </div>
                <div className={tokens.card}>
                  <div className="text-3xl mb-3">📋</div>
                  <h4 className="font-semibold text-white mb-2">Actionable Checklist</h4>
                  <p className="text-sm text-slate-400">Get specific compliance steps for your risk level</p>
                </div>
                <div className={tokens.card}>
                  <div className="text-3xl mb-3">🇪🇺</div>
                  <h4 className="font-semibold text-white mb-2">EU AI Act 2024</h4>
                  <p className="text-sm text-slate-400">Based on latest regulation (Aug 2026 deadline)</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );

  if (isAuthenticated === undefined) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <Layout skipTenantRedirect>
        <SEO
          title="AI Risk Classifier | EU AI Act Compliance | EvidentTrail"
          description="Classify your AI system under EU AI Act in 2 minutes. Get risk level and compliance requirements. Deadline: August 2, 2026."
        />
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-white mb-2">AI Risk Classifier</h1>
          <p className="text-slate-300">
            Classify your AI system and understand your compliance requirements. August 2, 2026 deadline approaching.
          </p>
        </div>
        {pageContent}
      </Layout>
    );
  }

  return (
    <MarketingLayout
      title="Free AI Risk Classifier | EU AI Act Compliance | EvidentTrail"
      description="Classify your AI system under EU AI Act in 2 minutes. Free tool determines risk level and compliance requirements. Deadline: August 2, 2026."
      h1="Free EU AI Act Risk Classifier"
      heroText="Classify your AI system and understand your compliance requirements. August 2, 2026 deadline approaching."
    >
      {pageContent}
    </MarketingLayout>
  );
}
